import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import ConsentActions from 'components/molecules/ConsentActions/ConsentActions';
import {
  AiInterviewConsentStatus,
  useAiInterviewConsentDeclineMutation,
  useAiInterviewConsentGiveMutation,
} from 'generated/graphql';
import styles from './ConsentGate.module.scss';

export interface ConsentGateProps {
  aiInterviewCandidateId: string;
  status: AiInterviewConsentStatus;
  consentText: string;
  companyName: string;
  /** Where "Back to my applications" leaves to. */
  exitUrl: string;
  /** Called once consent is recorded; the page refetches and moves on. */
  onConsented: () => void;
  /**
   * False once the interview has started: the server rejects a decline then,
   * so the form offers consent only.
   */
  canDecline?: boolean;
}

type View = 'form' | 'declined';

/**
 * The AI interview consent step (NIQ). Shown before the config wizard while
 * the company and role require consent and the candidate has not given it
 * for the current text. A decline parks the candidate on a holding screen
 * for every AI interview at that company until they change their mind.
 */
export function ConsentGate({
  aiInterviewCandidateId,
  status,
  consentText,
  companyName,
  exitUrl,
  onConsented,
  canDecline = true,
}: ConsentGateProps) {
  const [view, setView] = useState<View>(
    status === AiInterviewConsentStatus.Declined ? 'declined' : 'form'
  );

  return view === 'declined' ? (
    <ConsentDeclined
      companyName={companyName}
      exitUrl={exitUrl}
      onChangeDecision={() => setView('form')}
    />
  ) : (
    <ConsentForm
      aiInterviewCandidateId={aiInterviewCandidateId}
      consentText={consentText}
      companyName={companyName}
      onConsented={onConsented}
      onDeclined={canDecline ? () => setView('declined') : undefined}
    />
  );
}

function ConsentForm({
  aiInterviewCandidateId,
  consentText,
  companyName,
  onConsented,
  onDeclined,
}: {
  aiInterviewCandidateId: string;
  consentText: string;
  companyName: string;
  onConsented: () => void;
  /** Absent when declining is not offered. */
  onDeclined?: () => void;
}) {
  const { t } = useTranslation();
  const { handleMsgType } = useNotification();
  const variables = {
    aiInterviewAssessmentStageActionCandidateId: aiInterviewCandidateId,
  };
  const onError = (error: { message?: string }) =>
    handleMsgType({ type: TNotification.error, message: error?.message });

  const [giveConsent, { loading: giving }] = useAiInterviewConsentGiveMutation({
    variables,
    onCompleted: onConsented,
    onError,
  });
  const [declineConsent, { loading: declining }] =
    useAiInterviewConsentDeclineMutation({
      variables,
      onCompleted: onDeclined,
      onError,
    });

  return (
    <div className={styles.gate} data-testid="ai-consent__form">
      <div className={styles.header}>
        <h1 className={styles.title}>{t('aiInterview.consent.title')}</h1>
        <p className={styles.lead}>
          {t('aiInterview.consent.lead', { companyName })}
        </p>
      </div>
      <p className={styles.consentText}>{consentText}</p>
      <ConsentActions
        idPrefix="ai-consent"
        loading={giving || declining}
        checkboxLabel={t('aiInterview.consent.checkbox')}
        errorText={t('aiInterview.consent.error')}
        declineLabel={t('aiInterview.consent.decline')}
        confirmLabel={t('aiInterview.consent.confirm')}
        onConfirm={() => void giveConsent()}
        onDecline={onDeclined ? () => void declineConsent() : undefined}
      />
    </div>
  );
}

function ConsentDeclined({
  companyName,
  exitUrl,
  onChangeDecision,
}: {
  companyName: string;
  exitUrl: string;
  onChangeDecision: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={styles.gate} data-testid="ai-consent__declined">
      <div className={styles.header}>
        <h1 className={styles.title}>
          {t('aiInterview.consent.declinedTitle')}
        </h1>
        <p className={styles.lead}>
          {t('aiInterview.consent.declinedBody', { companyName })}
        </p>
      </div>
      <Button
        fullWidth
        onClick={() => navigate(exitUrl)}
        data-testid="ai-consent__back"
      >
        {t('aiInterview.consent.backToStages')}
      </Button>
      <button
        type="button"
        className={styles.changeDecision}
        onClick={onChangeDecision}
        data-testid="ai-consent__change-decision"
      >
        {t('aiInterview.consent.changeDecision')}
      </button>
    </div>
  );
}
